import {
  Directive,
  ElementRef,
  OnDestroy,
  HostListener,
  AfterContentInit, NgZone
} from '@angular/core';
import {
  ControlContainer,
  FormControl
} from '@angular/forms';
import {Subscription} from 'rxjs';

/**
 * Agriport directive implementation for any reactiveForm control.
 * This directive tracks the control's changes and triggers a blink animation
 * if the value changes programmatically.
 */
@Directive({
  selector: '[apControlBlinkOnChange]'
})
export class ApControlBlinkDirective implements OnDestroy, AfterContentInit {
  private readonly ANIMATION_NAME = 'ap-form-control-blink-animation';
  private control: FormControl | null = null;
  private subscription: Subscription | null = null;
  private isFocused = false;
  private previousValue = undefined;

  @HostListener('focus')
  public onFocus(): void {
    this.isFocused = true;
  }

  @HostListener('blur')
  public onBlur(): void {
    this.isFocused = false;
  }
  @HostListener('animationend')
  public onAnimationEnd(): void {
    this.el?.nativeElement?.classList?.remove(this.ANIMATION_NAME);
  }

  constructor(private el: ElementRef,
              private ngZone: NgZone,
              private controlContainer: ControlContainer) {
  }

  /**
   * Initialize tracking of changes for a single control and start animation if
   * control does not have the focus and the value changes
   */
  public ngAfterContentInit(): void {
    const name = this.el?.nativeElement?.getAttribute('id');
    if (name && this.controlContainer.control) {
      this.control = this.controlContainer?.control?.get(name) as FormControl;
    }

    if (this.control == null) {
      return;
    }

    // animations activated just a little bit later to avoid unwanted flickering on initialization
    setTimeout(() => {

      // store initial value to be able to compare it with later values
      this.previousValue = this.control?.value;

      // backup-strategy: subscribe to valueChanges (triggered when user changes values)
      // The 'registerOnChange' is not always reliable there (e.g. for ComboBoxes)
      this.control?.valueChanges?.subscribe(value => {
        // track latest value if the user changed it (has focus)
        // we need this later on to compare it with potentially current values
        if (this.isFocused ) {
          this.previousValue = value;
        }
      });

      // The registerOnChange is more robust for changes coming from the code.
      // Those indirect changes are supposed to be highlighted by a blink animation
      this.control?.registerOnChange((value: string) => {
        if (this.isFocused ) {
          // store current value (changed by user) to be able to compare it with later values
          this.previousValue = value;
          return;
        }

        // if values are equal => no animation
        if (this.getValueRepresentation(this.previousValue) === this.getValueRepresentation(value)) {
          return;
        }

        this.previousValue = value;
        this.startAnimation();
      });
    }, 1000);
  }

  /**
   * Starts the blink animation for our control
   * @private
   */
  private startAnimation(): void {
    this.ngZone.run(() => {
      this.el?.nativeElement?.classList?.remove(this.ANIMATION_NAME);
      const _ = this.el.nativeElement.offsetHeight; // Force repaint to trigger animation
      setTimeout(() => {
        this.el?.nativeElement?.classList?.add(this.ANIMATION_NAME);
      }, 0);
    });
  }

  /**
   * Gets uniform value representation of the value.
   * Undefined/null and empty string are treated as same values
   * (to avoid blinking when switching between those values)
   * @param value
   * @private
   */
  private getValueRepresentation(value: string): string {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return value;
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.control = undefined;
  }
}
